/**
 * G2eGente - Feed de Comunicados (Gestão pela Empresa)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { newsFeedAPI, employeesAPI, API_URL } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { 
  Plus, 
  Loader2,
  Newspaper,
  Megaphone,
  AlertTriangle,
  GraduationCap,
  Edit,
  Trash2,
  Eye,
  Users,
  Calendar,
  MoreHorizontal,
  CheckCircle,
  Clock,
  Send,
  X,
  BookOpen,
  Upload,
  Image,
  Link2,
  FileBarChart
} from 'lucide-react';
import { toast } from 'sonner';
import { storageAPI } from '../../lib/api';
import { ImageCropDialog } from '../../components/news/ImageCropDialog';
import { REACTIONS, ReactionCounts } from '../../components/news/reactions';
import { EngagementReportDialog } from '../../components/news/EngagementReportDialog';
import { EngagementStatsPanel } from '../../components/news/EngagementStatsPanel';

const TIPO_CONFIG = {
  noticia: { label: 'Notícia', icon: Newspaper, color: 'bg-blue-100 text-blue-700' },
  comunicado: { label: 'Comunicado', icon: Megaphone, color: 'bg-green-100 text-green-700' },
  alerta: { label: 'Alerta', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700' },
  treinamento: { label: 'Treinamento', icon: GraduationCap, color: 'bg-purple-100 text-purple-700' }
};

const PRIORIDADE_CONFIG = {
  normal: { label: 'Normal', color: 'bg-slate-100 text-slate-700' },
  alta: { label: 'Alta', color: 'bg-orange-100 text-orange-700' },
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-700' }
};

export default function FeedComunicados() {
  const [loading, setLoading] = useState(true);
  const [news, setNews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [setores, setSetores] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [tab, setTab] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [readsModal, setReadsModal] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Image upload states
  const [imagemTipo, setImagemTipo] = useState('url'); // 'url' ou 'upload'
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageCheck, setImageCheck] = useState(null);
  const [cropSource, setCropSource] = useState(null);
  const [readsTab, setReadsTab] = useState('leituras');
  const [reportNewsId, setReportNewsId] = useState(null);
  const closeReport = useCallback(() => setReportNewsId(null), []);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    titulo: '',
    conteudo: '',
    tipo: 'comunicado',
    prioridade: 'normal',
    imagem_capa: '',
    publico_alvo: { tipo: 'todos', usuarios: 'funcionarios' },
    publicado: true,
    data_expiracao: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [newsRes, empRes] = await Promise.all([
        newsFeedAPI.getAll(),
        employeesAPI.getAll()
      ]);
      setNews(newsRes.data);
      setEmployees(empRes.data || []);
      
      // Extrair setores e cargos únicos
      const uniqueSetores = [...new Set(empRes.data.map(e => e.setor).filter(Boolean))];
      const uniqueCargos = [...new Set(empRes.data.map(e => e.cargo).filter(Boolean))];
      setSetores(uniqueSetores);
      setCargos(uniqueCargos);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        titulo: item.titulo,
        conteudo: item.conteudo,
        tipo: item.tipo,
        prioridade: item.prioridade,
        imagem_capa: item.imagem_capa || '',
        publico_alvo: { 
          tipo: item.publico_alvo?.tipo || 'todos',
          usuarios: item.publico_alvo?.usuarios || 'funcionarios',
          setores: item.publico_alvo?.setores || [],
          cargos: item.publico_alvo?.cargos || [],
          funcionarios: item.publico_alvo?.funcionarios || []
        },
        publicado: item.publicado,
        data_expiracao: item.data_expiracao || ''
      });
      // Para o preview, usar URL completa se for URL interna
      const previewUrl = item.imagem_capa && item.imagem_capa.startsWith('/') 
        ? `${API_URL}${item.imagem_capa}` 
        : item.imagem_capa;
      setImagePreview(previewUrl || null);
      setImagemTipo(item.imagem_capa ? 'url' : 'url');
    } else {
      setEditingItem(null);
      setFormData({
        titulo: '',
        conteudo: '',
        tipo: 'comunicado',
        prioridade: 'normal',
        imagem_capa: '',
        publico_alvo: { tipo: 'todos', usuarios: 'funcionarios' },
        publicado: true,
        data_expiracao: ''
      });
      setImagePreview(null);
      setImagemTipo('upload');
    }
    setImageCheck(null);
    setModalOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImageCheck(null);

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      const msg = `Formato inválido (${file.type || 'desconhecido'}). Use JPG, PNG ou WebP.`;
      setImageCheck({ status: 'error', messages: [msg] });
      toast.error(msg);
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (file.size > 5 * 1024 * 1024) {
      const msg = `Arquivo muito grande (${fileSizeMB.toFixed(1)}MB). Máximo permitido: 5MB.`;
      setImageCheck({ status: 'error', messages: [msg] });
      toast.error(msg);
      return;
    }

    setCropSource({ src: URL.createObjectURL(file), name: file.name });
  };

  const closeCrop = () => {
    if (cropSource) URL.revokeObjectURL(cropSource.src);
    setCropSource(null);
  };

  const handleCropConfirm = async ({ file, sourceWidth }) => {
    const originalName = cropSource?.name;
    closeCrop();
    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('type', 'news_image');

      const res = await storageAPI.upload(formDataUpload);
      const { url: imageUrl, warnings = [], width, height, size_mb } = res.data;

      setFormData(prev => ({ ...prev, imagem_capa: imageUrl }));
      setImagePreview(imageUrl.startsWith('/') ? `${API_URL}${imageUrl}` : imageUrl);

      const allWarnings = [...warnings];
      if (sourceWidth < 1200) {
        allWarnings.push(`Resolução original baixa no recorte (${Math.round(sourceWidth)}px de largura). A imagem pode ficar desfocada; prefira originais com 1200px ou mais.`);
      }
      const info = `${originalName} — recortada para ${width}x${height}px, ${size_mb}MB`;
      if (allWarnings.length > 0) {
        setImageCheck({ status: 'warning', info, messages: allWarnings });
        toast.warning('Imagem carregada, mas com ressalvas');
      } else {
        setImageCheck({ status: 'ok', info, messages: ['Formato ideal para exibição no app.'] });
        toast.success('Imagem carregada!');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao carregar imagem';
      setImageCheck({ status: 'error', messages: [errorMsg] });
      toast.error(errorMsg);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUrlChange = (url) => {
    setFormData(prev => ({ ...prev, imagem_capa: url }));
    // Para URLs externas, usar diretamente; para URLs internas, adicionar API_URL
    const previewUrl = url && url.startsWith('/') ? `${API_URL}${url}` : url;
    setImagePreview(previewUrl || null);
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, imagem_capa: '' }));
    setImagePreview(null);
    setImageCheck(null);
  };

  const handleSave = async () => {
    if (!formData.titulo.trim() || !formData.conteudo.trim()) {
      toast.error('Título e conteúdo são obrigatórios');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        data_expiracao: formData.data_expiracao || null
      };

      if (editingItem) {
        await newsFeedAPI.update(editingItem.id, payload);
        toast.success('Comunicado atualizado!');
      } else {
        await newsFeedAPI.create(payload);
        toast.success('Comunicado criado!');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este comunicado?')) return;
    
    try {
      await newsFeedAPI.delete(id);
      toast.success('Comunicado excluído');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleTogglePublish = async (item) => {
    try {
      await newsFeedAPI.update(item.id, { publicado: !item.publicado });
      toast.success(item.publicado ? 'Comunicado despublicado' : 'Comunicado publicado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const loadReads = async (item) => {
    try {
      const [readsRes, reactRes] = await Promise.all([
        newsFeedAPI.getReads(item.id),
        newsFeedAPI.getReactions(item.id)
      ]);
      setReadsTab('leituras');
      setReadsModal({ item, reads: readsRes.data, reactions: reactRes.data });
    } catch (error) {
      toast.error('Erro ao carregar leituras');
    }
  };

  const filteredNews = tab === 'all' 
    ? news 
    : tab === 'publicados'
      ? news.filter(n => n.publicado)
      : news.filter(n => !n.publicado);

  const stats = {
    total: news.length,
    publicados: news.filter(n => n.publicado).length,
    rascunhos: news.filter(n => !n.publicado).length,
    totalLeituras: news.reduce((acc, n) => acc + (n.total_leituras || 0), 0)
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="feed-comunicados">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Feed de Comunicados</h1>
            <p className="text-slate-600">Comunique-se com seus funcionários</p>
          </div>
          <Button onClick={() => handleOpenModal()} className="bg-cyan-600 hover:bg-cyan-700" data-testid="novo-comunicado-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Comunicado
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Total</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Newspaper className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Publicados</p>
                  <p className="text-2xl font-bold text-green-600">{stats.publicados}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Rascunhos</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.rascunhos}</p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Total Leituras</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.totalLeituras}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs & List */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="all">Todos ({stats.total})</TabsTrigger>
            <TabsTrigger value="publicados">Publicados ({stats.publicados})</TabsTrigger>
            <TabsTrigger value="rascunhos">Rascunhos ({stats.rascunhos})</TabsTrigger>
            <TabsTrigger value="engajamento" data-testid="tab-engajamento">Engajamento</TabsTrigger>
          </TabsList>

          <TabsContent value="engajamento" className="mt-4">
            {tab === 'engajamento' && <EngagementStatsPanel onOpenReport={setReportNewsId} />}
          </TabsContent>

          {tab !== 'engajamento' && <TabsContent value={tab} className="mt-4">
            {filteredNews.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Nenhum comunicado
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Crie seu primeiro comunicado para manter sua equipe informada
                  </p>
                  <Button onClick={() => handleOpenModal()} className="bg-cyan-600 hover:bg-cyan-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Comunicado
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredNews.map(item => {
                  const TipoIcon = TIPO_CONFIG[item.tipo]?.icon || Newspaper;
                  return (
                    <Card key={item.id} className="border-slate-200 hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${TIPO_CONFIG[item.tipo]?.color || 'bg-slate-100'}`}>
                              <TipoIcon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h4 className="font-semibold text-slate-900 truncate">{item.titulo}</h4>
                                <Badge className={TIPO_CONFIG[item.tipo]?.color || 'bg-slate-100'}>
                                  {TIPO_CONFIG[item.tipo]?.label || item.tipo}
                                </Badge>
                                <Badge className={PRIORIDADE_CONFIG[item.prioridade]?.color || 'bg-slate-100'}>
                                  {PRIORIDADE_CONFIG[item.prioridade]?.label || item.prioridade}
                                </Badge>
                                {!item.publicado && (
                                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                                    Rascunho
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-600 line-clamp-2">{item.conteudo}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {item.data_publicacao ? new Date(item.data_publicacao).toLocaleDateString('pt-BR') : 'Não publicado'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {item.total_destinatarios || 0} destinatários
                                </span>
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" />
                                  {item.total_leituras || 0} leituras
                                </span>
                                <span className="flex items-center gap-1 text-cyan-700 font-medium" data-testid={`comunicado-engajamento-${item.id}`}>
                                  {item.total_reacoes || 0} reações · {item.total_destinatarios ? Math.round((item.total_reacoes || 0) / item.total_destinatarios * 1000) / 10 : 0}% engajamento
                                </span>
                              </div>
                              <div className="mt-2">
                                <ReactionCounts reacoes={item.reacoes} testIdPrefix={`comunicado-reacoes-${item.id}`} />
                              </div>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`comunicado-menu-${item.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem data-testid={`comunicado-visualizar-${item.id}`} onClick={() => setDetailModal(item)}>
                                <Eye className="w-4 h-4 mr-2" />
                                Visualizar
                              </DropdownMenuItem>
                              <DropdownMenuItem data-testid={`comunicado-leituras-${item.id}`} onClick={() => loadReads(item)}>
                                <BookOpen className="w-4 h-4 mr-2" />
                                Leituras e Reações
                              </DropdownMenuItem>
                              <DropdownMenuItem data-testid={`comunicado-relatorio-${item.id}`} onClick={() => setReportNewsId(item.id)}>
                                <FileBarChart className="w-4 h-4 mr-2" />
                                Relatório de Engajamento
                              </DropdownMenuItem>
                              <DropdownMenuItem data-testid={`comunicado-editar-${item.id}`} onClick={() => handleOpenModal(item)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem data-testid={`comunicado-publicar-toggle-${item.id}`} onClick={() => handleTogglePublish(item)}>
                                {item.publicado ? (
                                  <>
                                    <X className="w-4 h-4 mr-2" />
                                    Despublicar
                                  </>
                                ) : (
                                  <>
                                    <Send className="w-4 h-4 mr-2" />
                                    Publicar
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDelete(item.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>}
        </Tabs>

        {/* Modal Criar/Editar */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? 'Editar Comunicado' : 'Novo Comunicado'}
              </DialogTitle>
              <DialogDescription>
                {editingItem ? 'Edite os dados do comunicado abaixo' : 'Preencha os dados para criar um novo comunicado'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
                    <SelectTrigger data-testid="tipo-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="noticia">Notícia</SelectItem>
                      <SelectItem value="comunicado">Comunicado</SelectItem>
                      <SelectItem value="alerta">Alerta</SelectItem>
                      <SelectItem value="treinamento">Treinamento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Prioridade</Label>
                  <Select value={formData.prioridade} onValueChange={(v) => setFormData({...formData, prioridade: v})}>
                    <SelectTrigger data-testid="prioridade-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                      <SelectItem value="urgente">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Título *</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                  placeholder="Título do comunicado"
                  data-testid="titulo-input"
                />
              </div>

              <div>
                <Label>Conteúdo *</Label>
                <Textarea
                  value={formData.conteudo}
                  onChange={(e) => setFormData({...formData, conteudo: e.target.value})}
                  placeholder="Escreva o conteúdo do comunicado..."
                  rows={6}
                  data-testid="conteudo-textarea"
                />
              </div>

              {/* Imagem de Capa - Upload ou URL */}
              <div className="space-y-3">
                <Label>Imagem de Capa (Campanha)</Label>
                
                {/* Tabs para escolher tipo */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={imagemTipo === 'upload' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setImagemTipo('upload')}
                    className={imagemTipo === 'upload' ? 'bg-cyan-600' : ''}
                    data-testid="imagem-tipo-upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                  <Button
                    type="button"
                    variant={imagemTipo === 'url' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setImagemTipo('url')}
                    className={imagemTipo === 'url' ? 'bg-cyan-600' : ''}
                    data-testid="imagem-tipo-url-btn"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    URL Externa
                  </Button>
                </div>

                {/* Upload de arquivo */}
                {imagemTipo === 'upload' && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploadingImage}
                          data-testid="imagem-upload-input"
                        />
                        <div className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-cyan-500 hover:bg-cyan-50 transition-colors">
                          {uploadingImage ? (
                            <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />
                          ) : (
                            <Image className="w-5 h-5 text-slate-400" />
                          )}
                          <span className="text-sm text-slate-600">
                            {uploadingImage ? 'Carregando...' : 'Clique para selecionar imagem'}
                          </span>
                        </div>
                      </label>
                    </div>
                    {/* Orientações de formato */}
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs" data-testid="imagem-orientacoes">
                      <p className="font-medium text-blue-800 mb-1">Orientações para melhor exibição:</p>
                      <ul className="text-blue-700 space-y-0.5">
                        <li>• <strong>Proporção:</strong> 16:9 — após selecionar, você poderá recortar e ajustar a imagem</li>
                        <li>• <strong>Resolução recomendada:</strong> 1200 x 675 px ou maior (saída final: 1200 x 675 px)</li>
                        <li>• <strong>Formatos aceitos:</strong> JPG, PNG, WebP</li>
                        <li>• <strong>Tamanho máximo:</strong> 5MB</li>
                      </ul>
                    </div>
                  </div>
                )}

                {imageCheck && (
                  <div
                    data-testid="imagem-validacao"
                    data-status={imageCheck.status}
                    className={`p-3 rounded-lg border text-xs ${
                      imageCheck.status === 'error' ? 'bg-red-50 border-red-200 text-red-700'
                      : imageCheck.status === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800'
                      : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    }`}
                  >
                    <p className="font-medium">
                      {imageCheck.status === 'error' ? 'Imagem recusada' : imageCheck.status === 'warning' ? 'Imagem aceita com ressalvas' : 'Imagem validada'}
                      {imageCheck.info ? ` — ${imageCheck.info}` : ''}
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {imageCheck.messages.map((m, i) => <li key={i}>• {m}</li>)}
                    </ul>
                  </div>
                )}

                {/* URL externa */}
                {imagemTipo === 'url' && (
                  <Input
                    value={formData.imagem_capa}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    placeholder="https://exemplo.com/imagem.jpg"
                    data-testid="imagem-url-input"
                  />
                )}

                {/* Preview da imagem */}
                {imagePreview && (
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-48 object-cover rounded-lg border"
                      onError={() => setImagePreview(null)}
                      data-testid="imagem-preview"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={handleRemoveImage}
                      data-testid="imagem-remover-btn"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <Label>Destinatários</Label>
                <Select 
                  value={formData.publico_alvo.usuarios || 'funcionarios'} 
                  onValueChange={(v) => setFormData({
                    ...formData, 
                    publico_alvo: { ...formData.publico_alvo, usuarios: v }
                  })}
                >
                  <SelectTrigger data-testid="usuarios-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos (Funcionários + Candidatos)</SelectItem>
                    <SelectItem value="funcionarios">Apenas Funcionários</SelectItem>
                    <SelectItem value="candidatos">Apenas Candidatos</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Define quem pode visualizar este comunicado no app
                </p>
              </div>

              <div>
                <Label>Público-Alvo (Detalhamento)</Label>
                <Select 
                  value={formData.publico_alvo.tipo} 
                  onValueChange={(v) => setFormData({
                    ...formData, 
                    publico_alvo: { ...formData.publico_alvo, tipo: v, setores: [], cargos: [], funcionarios: [] }
                  })}
                  disabled={formData.publico_alvo.usuarios === 'candidatos'}
                >
                  <SelectTrigger data-testid="publico-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os Funcionários</SelectItem>
                    <SelectItem value="setor">Por Setor</SelectItem>
                    <SelectItem value="cargo">Por Cargo</SelectItem>
                    <SelectItem value="especifico">Funcionários Específicos</SelectItem>
                  </SelectContent>
                </Select>
                {formData.publico_alvo.usuarios === 'candidatos' && (
                  <p className="text-xs text-amber-600 mt-1">
                    Detalhamento não aplicável para candidatos
                  </p>
                )}

                {formData.publico_alvo.tipo === 'setor' && (
                  <div className="mt-2">
                    <Label className="text-sm">Selecione os setores</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {setores.map(setor => (
                        <Badge
                          key={setor}
                          variant={formData.publico_alvo.setores?.includes(setor) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => {
                            const current = formData.publico_alvo.setores || [];
                            const updated = current.includes(setor)
                              ? current.filter(s => s !== setor)
                              : [...current, setor];
                            setFormData({
                              ...formData,
                              publico_alvo: { ...formData.publico_alvo, setores: updated }
                            });
                          }}
                        >
                          {setor}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {formData.publico_alvo.tipo === 'cargo' && (
                  <div className="mt-2">
                    <Label className="text-sm">Selecione os cargos</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {cargos.map(cargo => (
                        <Badge
                          key={cargo}
                          variant={formData.publico_alvo.cargos?.includes(cargo) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => {
                            const current = formData.publico_alvo.cargos || [];
                            const updated = current.includes(cargo)
                              ? current.filter(c => c !== cargo)
                              : [...current, cargo];
                            setFormData({
                              ...formData,
                              publico_alvo: { ...formData.publico_alvo, cargos: updated }
                            });
                          }}
                        >
                          {cargo}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {formData.publico_alvo.tipo === 'especifico' && (
                  <div className="mt-2">
                    <Label className="text-sm">Selecione os funcionários</Label>
                    <div className="max-h-40 overflow-y-auto border rounded p-2 mt-1">
                      {employees.filter(e => e.situacao === 'ativo').map(emp => (
                        <label key={emp.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.publico_alvo.funcionarios?.includes(emp.id) || false}
                            onChange={(e) => {
                              const current = formData.publico_alvo.funcionarios || [];
                              const updated = e.target.checked
                                ? [...current, emp.id]
                                : current.filter(id => id !== emp.id);
                              setFormData({
                                ...formData,
                                publico_alvo: { ...formData.publico_alvo, funcionarios: updated }
                              });
                            }}
                          />
                          <span className="text-sm">{emp.nome} ({emp.cargo})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data de Expiração (opcional)</Label>
                  <Input
                    type="datetime-local"
                    value={formData.data_expiracao?.slice(0, 16) || ''}
                    onChange={(e) => setFormData({
                      ...formData, 
                      data_expiracao: e.target.value ? new Date(e.target.value).toISOString() : ''
                    })}
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.publicado}
                      onChange={(e) => setFormData({...formData, publicado: e.target.checked})}
                    />
                    <span>Publicar imediatamente</span>
                  </label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving || uploadingImage} className="bg-cyan-600 hover:bg-cyan-700" data-testid="salvar-comunicado-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingItem ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Visualizar */}
        <Dialog open={!!detailModal} onOpenChange={() => setDetailModal(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Visualizar Comunicado</DialogTitle>
            </DialogHeader>
            {detailModal && (
              <div className="space-y-4">
                {detailModal.imagem_capa && (
                  <img 
                    src={detailModal.imagem_capa.startsWith('/') ? `${API_URL}${detailModal.imagem_capa}` : detailModal.imagem_capa} 
                    alt="" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                )}
                <div className="flex items-center gap-2">
                  <Badge className={TIPO_CONFIG[detailModal.tipo]?.color}>
                    {TIPO_CONFIG[detailModal.tipo]?.label}
                  </Badge>
                  <Badge className={PRIORIDADE_CONFIG[detailModal.prioridade]?.color}>
                    {PRIORIDADE_CONFIG[detailModal.prioridade]?.label}
                  </Badge>
                  {!detailModal.publicado && (
                    <Badge variant="outline" className="text-amber-600">Rascunho</Badge>
                  )}
                </div>
                <h2 className="text-xl font-bold text-slate-900">{detailModal.titulo}</h2>
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap">{detailModal.conteudo}</p>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-500 pt-4 border-t">
                  <span>Criado em: {new Date(detailModal.created_at).toLocaleString('pt-BR')}</span>
                  <span>Por: {detailModal.created_by_nome || 'Sistema'}</span>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal Leituras e Reações */}
        <Dialog open={!!readsModal} onOpenChange={() => setReadsModal(null)}>
          <DialogContent className="max-w-lg" data-testid="reads-modal">
            <DialogHeader>
              <DialogTitle>Leituras e Reações</DialogTitle>
              <DialogDescription>{readsModal?.item.titulo}</DialogDescription>
            </DialogHeader>
            {readsModal && (
              <Tabs value={readsTab} onValueChange={setReadsTab}>
                <TabsList className="w-full">
                  <TabsTrigger value="leituras" className="flex-1" data-testid="reads-tab-leituras">
                    Leituras ({readsModal.reads.length})
                  </TabsTrigger>
                  <TabsTrigger value="reacoes" className="flex-1" data-testid="reads-tab-reacoes">
                    Reações ({readsModal.reactions.total_reacoes})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="leituras">
                  {readsModal.reads.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">Nenhuma leitura registrada</p>
                  ) : (
                    <div className="max-h-80 overflow-y-auto space-y-2">
                      {readsModal.reads.map((read, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <div className="font-medium text-slate-900">
                              {read.employee_nome}
                              <Badge variant="outline" className="ml-2 text-[10px]">{read.publico === 'candidato' ? 'Candidato' : 'Funcionário'}</Badge>
                            </div>
                            <p className="text-xs text-slate-500">{read.employee_cargo || '-'}{read.employee_setor ? ` • ${read.employee_setor}` : ''}</p>
                          </div>
                          <span className="text-xs text-slate-500">
                            {new Date(read.data_leitura).toLocaleString('pt-BR')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="reacoes">
                  <ReactionsPanel data={readsModal.reactions} />
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        <EngagementReportDialog newsId={reportNewsId} onClose={closeReport} />

        <ImageCropDialog
          src={cropSource?.src}
          fileName={cropSource?.name}
          onCancel={closeCrop}
          onConfirm={handleCropConfirm}
        />
      </div>
    </DashboardLayout>
  );
}

function ReactionsPanel({ data }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-2">
        {REACTIONS.map(({ tipo, label, icon: Icon, color, bg }) => (
          <div key={tipo} className={`p-2 rounded-lg border text-center ${bg}`} data-testid={`reactions-total-${tipo}`}>
            <Icon className={`w-4 h-4 mx-auto ${color}`} />
            <p className={`text-lg font-bold ${color}`}>{data.reacoes[tipo] || 0}</p>
            <p className="text-[11px] text-slate-600">{label}</p>
          </div>
        ))}
      </div>
      <p className="text-sm text-slate-600" data-testid="reactions-engajamento">
        <strong>{data.taxa_engajamento}%</strong> de engajamento ({data.total_reacoes} de {data.total_destinatarios} destinatários)
      </p>
      {data.items.length === 0 ? (
        <p className="text-center py-6 text-slate-500">Nenhuma reação registrada</p>
      ) : (
        <div className="max-h-64 overflow-y-auto space-y-2">
          {data.items.map((r) => {
            const cfg = REACTIONS.find(x => x.tipo === r.tipo);
            const Icon = cfg?.icon;
            return (
              <div key={`${r.publico}-${r.actor_id}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`reaction-item-${r.employee_id}`}>
                <div>
                  <div className="font-medium text-slate-900">
                    {r.employee_nome}
                    <Badge variant="outline" className="ml-2 text-[10px]">{r.publico === 'candidato' ? 'Candidato' : 'Funcionário'}</Badge>
                  </div>
                  <p className="text-xs text-slate-500">{r.employee_cargo || '-'}{r.employee_setor ? ` • ${r.employee_setor}` : ''}</p>
                </div>
                {cfg && (
                  <span className={`flex items-center gap-1 text-xs font-medium ${cfg.color}`}>
                    <Icon className="w-4 h-4" /> {cfg.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
